// src/components/RecentResultsSlider.tsx
import React, { useEffect, useRef, useState } from "react";
import { API_BASE } from "lib/http"; // 共通の API_BASE を使用（/public 直下へつなぐ）

type ResultItem = {
  id: string | number;
  date: string;
  raceName: string;
  planName?: string;
  amountJPY: number;
  bet: string;
  style?: "gold" | "silver";
  isPublished?: boolean;
  sort?: number;
};

export default function RecentResultsSlider() {
  const [items, setItems] = useState<ResultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const railRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        // ✅ /public/results から取得（server/index.js 側で /api を剥がしている）
        const r = await fetch(`${API_BASE}/public/results?t=${Date.now()}`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const j: any = await r.json();

        // 🔧 戻りが {items:[...]} / [...] 両対応
        const raw: any[] = Array.isArray(j?.items)
          ? j.items
          : Array.isArray(j)
          ? j
          : [];

        const mapped: ResultItem[] = raw.map((x: any, i: number) => ({
          id: x.id ?? i,
          date: String(x.date ?? ""),
          raceName: String(x.raceName ?? x.title ?? ""),
          planName: String(x.planName ?? x.plan ?? ""),
          amountJPY: Number(x.amount ?? x.amountJPY ?? x.priceJPY ?? 0),
          bet: String(x.bet ?? x.item ?? x.body ?? ""),
          style:
            x.badge === "SILVER" || x.style === "silver" ? "silver" : "gold",
          isPublished: x.publish === false ? false : x.isPublished !== false,
          sort: Number(x.sort ?? 0),
        }));

        // 公開中のみ
        let filtered = mapped.filter((x) => x.isPublished !== false);

        // ✅ 当日〜2日前までに絞る（日本時間ベース）
        const today = new Date();
        const todayJST = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );

        filtered = filtered.filter((x) => {
          const d = parseLocalDate(x.date);
          if (!d) return false;
          const diffMs = todayJST.getTime() - d.getTime();
          const diffDays = diffMs / (1000 * 60 * 60 * 24);
          // 0〜2日以内（未来日は除外）
          return diffDays >= 0 && diffDays <= 2;
        });

        // 並び順：sort 降順 → 日付降順 → id 降順
        const sorted = filtered.sort((a, b) => {
          const sa = a.sort ?? 0;
          const sb = b.sort ?? 0;
          if (sb !== sa) return sb - sa;

          const da = parseLocalDate(a.date)?.getTime() ?? 0;
          const db = parseLocalDate(b.date)?.getTime() ?? 0;
          if (db !== da) return db - da;

          return String(b.id).localeCompare(String(a.id));
        });

        if (!abort) setItems(sorted.slice(0, 12)); // 最大12件
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[RecentResultsSlider] fetch failed:", e);
        if (!abort) setItems([]);
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, []);

  const scrollByCard = (dir: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector<HTMLElement>("[data-card]");
    const step = card ? card.offsetWidth + 12 : 220;
    rail.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section style={{ padding: "8px 12px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
          直近の実績
        </h2>
        <a
          href="/results"
          style={{
            fontSize: 12,
            color: "#1976d2",
            textDecoration: "none",
            fontWeight: 700,
          }}
        >
          実績をもっと見る →
        </a>
      </div>

      {/* スライダー全体ラッパー（中央寄せ） */}
      <div
        style={{
          position: "relative",
          maxWidth: 260,
          margin: "0 auto",
        }}
      >
        {/* スクロールレール */}
        <div
          ref={railRef}
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            paddingBottom: 6,
            scrollSnapType: "x mandatory",
          }}
        >
          {loading && <div style={{ padding: 10 }}>読み込み中...</div>}
          {!loading && items.map((it) => <Card key={it.id} item={it} />)}
          {!loading && items.length === 0 && (
            <div style={{ padding: 10, color: "#667" }}>
              実績はまだありません。
            </div>
          )}
        </div>

        {/* ナビボタン */}
        <button
          aria-label="左へ"
          onClick={() => scrollByCard(-1)}
          style={navBtn("left")}
        >
          ◀
        </button>
        <button
          aria-label="右へ"
          onClick={() => scrollByCard(1)}
          style={navBtn("right")}
        >
          ▶
        </button>
      </div>
    </section>
  );
}

/* =========================
 * カード表示
 * ========================= */
function Card({ item }: { item: ResultItem }) {
  const img =
    item.style === "silver"
      ? "/assets/member/jisseki_silver_big.webp"
      : "/assets/member/jisseki_gold_big.webp";

  return (
    <div
      data-card
      style={{
        minWidth: 220,
        maxWidth: 220,
        flex: "0 0 220px",
        position: "relative",
        scrollSnapAlign: "center",
        margin: "0 auto",
      }}
    >
      <div style={{ position: "relative", textAlign: "center" }}>
        <img
          src={img}
          alt="実績バッジ"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25))",
            borderRadius: 12,
          }}
          loading="lazy"
        />

        {/* 🔶 上部の金プレート：レース名 */}
        <div
          style={{
            position: "absolute",
            top: "6%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "80%",
            fontSize: 14,
            fontWeight: 800,
            color: "#333",
            textShadow: "0 1px 0 #fff",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.raceName}
        </div>

        {/* 🔶 盾中央：プラン名・日付・金額・買い目 */}
<div
  style={{
    position: "absolute",
    top: "30%", // ← ここを 38% → 30% に変更（さらに上へ）
    left: "50%",
    transform: "translateX(-50%)",
    width: "75%",
    lineHeight: 1.2,
    color: "#333",
    textShadow: "0 1px 0 #fff",
    fontWeight: 900,
  }}
>

          {/* プラン名 */}
          {item.planName && (
            <div
              style={{
                fontSize: 14,
                marginBottom: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.planName}
            </div>
          )}

          {/* 日付 */}
          <div style={{ fontSize: 13, marginBottom: 2 }}>
            {formatDateJP(item.date)}
          </div>

          {/* 金額 */}
          <div style={{ fontSize: 18, marginTop: 4 }}>
            {formatJPY(item.amountJPY)}
          </div>

          {/* 買い目（3連単 4-5-3 など） */}
          {item.bet && (
            <div style={{ fontSize: 13, marginTop: 2 }}>{item.bet}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================
 * ヘルパー
 * ========================= */

function navBtn(pos: "left" | "right"): React.CSSProperties {
  const base: React.CSSProperties = {
    position: "absolute",
    top: "40%",
    transform: "translateY(-50%)",
    border: "1px solid #999",
    background: "#fff",
    borderRadius: 999,
    width: 32,
    height: 32,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
    opacity: 0.9,
  };
  return pos === "left" ? { ...base, left: -6 } : { ...base, right: -6 };
}

// "2025-12-04" → ローカル日付として解釈
function parseLocalDate(s?: string): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  return new Date(y, mo, d);
}

function formatJPY(v?: number) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toLocaleString("ja-JP")}円`;
}

function formatDateJP(s?: string) {
  const d = parseLocalDate(s);
  if (!d) return s || "";
  const w = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}(${w})`;
}
